<template>
  <dashboard-collapsible-card
    title="Sondages en cours"
    :collapsed="collapsed"
    header-class="flex items-start justify-between gap-3"
    collapsed-icon="fas fa-poll-h text-primary-600"
    :card-class="computedCardClass"
    content-class="mt-3 flex-1 flex flex-col space-y-5"
    @toggle="$emit('toggle')"
  >
    <template #header-actions>
      <dashboard-metric-chip
        class="shrink-0"
        label="Total"
        :value="formatNumber(totalActive)"
        tooltip="Sondages actifs"
      />
    </template>
    <template #collapsed-description>
      <p class="text-xs text-gray-500">
        {{ formatNumber(totalActive) }} en cours
      </p>
    </template>

    <section class="space-y-2 flex-1 flex flex-col text-[0.8125rem]">
      <dashboard-list-state
        :loading="loadingActive"
        :has-data="activeSurveys.length"
        class="flex-1 flex flex-col"
      >
        <template #default>
          <div class="space-y-2">
            <div
              v-for="(survey, index) in activeSurveys"
              :key="survey.uuid || survey.exportId || index"
              class="rounded-2xl border border-primary-100 bg-opacity-40 px-3 py-2.5"
            >
              <dashboard-tooltip-pill
                variant="text"
                :tooltip="survey.title"
                class="dashboard-survey-title-pill cursor-default"
              >
                <span class="dashboard-survey-title text-[0.8125rem] font-medium text-gray-900 leading-snug">
                  {{ survey.title }}
                </span>
              </dashboard-tooltip-pill>
              <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-gray-600">
                <dashboard-tooltip-pill
                  variant="text"
                  :tooltip="answerTooltip('complete', survey.stats.fullAnswers)"
                  class="dashboard-survey-stat"
                >
                  <span>
                    {{ formatNumber(survey.stats.fullAnswers) }} complètes
                    <span
                      v-if="formatPercentage(survey.stats.fullAnswers)"
                      class="text-gray-400"
                    >
                      ({{ formatPercentage(survey.stats.fullAnswers) }})
                    </span>
                  </span>
                </dashboard-tooltip-pill>
                <span class="text-gray-300" aria-hidden="true">·</span>
                <dashboard-tooltip-pill
                  variant="text"
                  :tooltip="answerTooltip('partial', survey.stats.partialAnswers)"
                  class="dashboard-survey-stat"
                >
                  <span>
                    {{ formatNumber(survey.stats.partialAnswers) }} partielles
                    <span
                      v-if="formatPercentage(survey.stats.partialAnswers)"
                      class="text-gray-400"
                    >
                      ({{ formatPercentage(survey.stats.partialAnswers) }})
                    </span>
                  </span>
                </dashboard-tooltip-pill>
              </p>
            </div>
          </div>
        </template>
        <template #loading>
          <div class="space-y-2">
            <div
              v-for="index in 3"
              :key="index"
              class="space-y-2 rounded-2xl border border-dashed border-primary-100 bg-white px-4 py-3"
            >
              <div class="dashboard-skeleton h-4 w-2/3 rounded-full"></div>
              <div class="dashboard-skeleton h-3 w-1/2 rounded-full"></div>
              <div class="dashboard-skeleton h-3 w-1/3 rounded-full"></div>
            </div>
          </div>
        </template>
        <template #empty>
          <div class="flex flex-1 items-center justify-center rounded-2xl bg-gray-100 px-4 py-6 text-sm text-gray-600">
            Aucun sondage à afficher
          </div>
        </template>
      </dashboard-list-state>
    </section>

    <!-- <section class="space-y-3">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Dernier sondage terminé</h4>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/80 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-primary-500 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!lastEndedSurvey || !lastEndedSurvey.uuid || downloadingStats"
          @click="$emit('download-stats')"
        >
          <i class="fas fa-download" v-if="!downloadingStats"></i>
          <i class="fas fa-spinner fa-spin" v-else></i>
          <span>Télécharger les stats</span>
        </button>
      </div>
      <div v-if="loadingActive" class="space-y-3 rounded-xl border border-dashed border-gray-200 bg-white px-4 py-4">
        <div class="dashboard-skeleton h-4 w-2/3 rounded-full"></div>
        <div class="dashboard-skeleton h-3 w-1/2 rounded-full"></div>
        <div class="dashboard-skeleton h-3 w-1/3 rounded-full"></div>
      </div>
      <dashboard-list-state
        v-else
        :loading="false"
        :has-data="Boolean(lastEndedSurvey)"
      >
        <template #default>
          <div class="rounded-xl border border-gray-200 bg-white/80 px-4 py-4 shadow-inner">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div class="space-y-1">
                <p class="text-sm text-gray-900">{{ lastEndedSurvey.title }}</p>
                <p v-if="lastEndedSurvey.periodLabel" class="text-xs text-gray-500">{{ lastEndedSurvey.periodLabel }}</p>
                <p v-if="lastEndedSurvey.endLabel" class="text-xs text-gray-500">Clôturé le {{ lastEndedSurvey.endLabel }}</p>
              </div>
              <div class="flex flex-wrap items-start justify-end gap-2">
                <dashboard-metric-chip
                  class="shrink-0"
                  label="Complètes"
                  :value="formatNumber(lastEndedSurvey.stats.fullAnswers)"
                  :tooltip="answerTooltip('réponses complètes', lastEndedSurvey.stats.fullAnswers)"
                />
                <dashboard-metric-chip
                  class="shrink-0"
                  label="Partielles"
                  :value="formatNumber(lastEndedSurvey.stats.partialAnswers)"
                  :tooltip="answerTooltip('réponses partielles', lastEndedSurvey.stats.partialAnswers)"
                />
              </div>
            </div>
          </div>
        </template>
        <template #empty>
          <div class="flex items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-100 px-4 py-6 text-sm text-gray-600">
            Aucune statistique disponible
          </div>
        </template>
      </dashboard-list-state>
    </section> -->
  </dashboard-collapsible-card>
</template>

<script>
import DashboardCollapsibleCard from '@/components/dashboard/DashboardCollapsibleCard.vue'
import DashboardListState from '@/components/dashboard/DashboardListState.vue'
import DashboardMetricChip from '@/components/dashboard/DashboardMetricChip.vue'
import DashboardTooltipPill from '@/components/dashboard/DashboardTooltipPill.vue'

export default {
  name: 'DashboardActiveSurveysCard',
  components: {
    DashboardCollapsibleCard,
    DashboardListState,
    DashboardMetricChip,
    DashboardTooltipPill
  },
  props: {
    collapsed: {
      type: Boolean,
      required: true
    },
    activeSurveys: {
      type: Array,
      default: () => []
    },
    totalActive: {
      type: Number,
      default: 0
    },
    loadingActive: {
      type: Boolean,
      default: false
    },
    lastEndedSurvey: {
      type: Object,
      default: null
    },
    downloadingStats: {
      type: Boolean,
      default: false
    },
    workforceSize: {
      type: Number,
      default: null
    },
    cardClass: {
      type: String,
      default: ''
    },
    formatNumber: {
      type: Function,
      required: true
    }
  },
  emits: ['toggle', 'download-stats'],
  computed: {
    computedCardClass () {
      return ['h-full flex flex-col', this.cardClass].filter(Boolean).join(' ')
    }
  },
  methods: {
    formatPercentage (count) {
      if (!this.workforceSize || this.workforceSize <= 0) return null
      const ratio = count / this.workforceSize
      if (ratio === 0) return '0%'
      const pct = (ratio * 100)
      const value = pct < 0.1 && pct > 0 ? '<0,1' : pct.toFixed(1).replace('.', ',')
      return `${value}%`
    },
    answerTooltip (type, count) {
      const formattedCount = this.formatNumber(count)
      const pct = this.formatPercentage(count)
      const suffix = pct ? `, ${pct} des utilisateurs` : ''

      if (type === 'complete') {
        const noun = Number(count) > 1 ? 'réponses complètes' : 'réponse complète'
        return `${formattedCount} ${noun} au sondage${suffix}`
      }

      if (type === 'partial') {
        const noun = Number(count) > 1 ? 'réponses partielles' : 'réponse partielle'
        return `${formattedCount} ${noun} au sondage${suffix}`
      }

      return `${formattedCount} réponses au sondage${suffix}`
    }
  }
}
</script>

<style scoped>
.dashboard-survey-title-pill {
  display: block;
  min-width: 0;
}

.dashboard-survey-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dashboard-survey-stat {
  color: inherit;
  gap: 0.25rem;
}

.dashboard-survey-stat :deep(span) {
  display: inline;
}
</style>
